import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import roster from '../rosterData';
import allReleases from '../releases';
import { FaInstagram, FaSpotify, FaApple, FaTiktok, FaYoutube } from 'react-icons/fa';
import DistributionFormSigner from './DistributionFormSigner';

const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'YOUR_CLOUD_NAME';
const CLOUDINARY_UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'YOUR_UNSIGNED_PRESET';


/* ── Theme presets ── */
const THEME_PRESETS = [
  { id: 'dark',        label: 'Void',       bg: '#000000', fg: '#f0ede8', preview: ['#000','#f0ede8'] },
  { id: 'light',       label: 'Paper',      bg: '#f5f3ef', fg: '#0a0a0a', preview: ['#f5f3ef','#0a0a0a'] },
  { id: 'slate',       label: 'Slate',      bg: '#0f1117', fg: '#e8eaf0', preview: ['#0f1117','#e8eaf0'] },
  { id: 'crimson',     label: 'Crimson',    bg: '#0d0305', fg: '#f5c6c0', preview: ['#0d0305','#f5c6c0'] },
  { id: 'forest',      label: 'Forest',     bg: '#050d07', fg: '#c8e6c0', preview: ['#050d07','#c8e6c0'] },
  { id: 'dusk',        label: 'Dusk',       bg: '#0e0812', fg: '#e8d4f5', preview: ['#0e0812','#e8d4f5'] },
  { id: 'sand',        label: 'Sand',       bg: '#f2ede4', fg: '#2a1f0a', preview: ['#f2ede4','#2a1f0a'] },
  { id: 'midnight',    label: 'Midnight',   bg: '#080c1a', fg: '#c8d4f0', preview: ['#080c1a','#c8d4f0'] },
];

function themeColors(themeId) {
  const preset = THEME_PRESETS.find(t => t.id === themeId);
  if (preset) return { bg: preset.bg, fg: preset.fg };
  return { bg: '#000000', fg: '#f0ede8' };
}

async function uploadSquarePhoto(file, folder, onProgress) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
  formData.append('folder', folder);
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`);
    xhr.upload.onprogress = e => { if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100)); };
    xhr.onload = () => {
      if (xhr.status === 200) {
        const d = JSON.parse(xhr.responseText);
        resolve(d.secure_url.replace('/upload/', '/upload/c_fill,g_face,w_600,h_600,f_auto,q_auto/'));
      } else reject(new Error('Upload failed'));
    };
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.send(formData);
  });
}

const F = "'Helvetica Neue', Helvetica, Arial, sans-serif";

const ICON_OPTIONS = [
  { value: '',        label: 'None' },
  { value: 'spotify', label: 'Spotify' },
  { value: 'apple',   label: 'Apple Music' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'link',    label: 'Link' },
  { value: 'cd',      label: 'CD' },
];
const ICON_RENDER = {
  spotify: <FaSpotify size={13} />,
  apple:   <FaApple size={13} />,
  youtube: <FaYoutube size={13} />,
  link:    <span style={{ fontSize: '13px' }}>—</span>,
  cd:      <span style={{ fontSize: '13px' }}>◎</span>,
};

const DEFAULT_ORDER = ['spotify','appleMusic','youtube','tiktok','instagram','press'];
const PLATFORM_META = {
  spotify:    { label: 'SPOTIFY',     icon: <FaSpotify size={15} />   },
  appleMusic: { label: 'APPLE MUSIC', icon: <FaApple size={15} />     },
  youtube:    { label: 'YOUTUBE',     icon: <FaYoutube size={15} />   },
  tiktok:     { label: 'TIKTOK',      icon: <FaTiktok size={15} />    },
  instagram:  { label: 'INSTAGRAM',   icon: <FaInstagram size={15} /> },
  press:      { label: 'PRESS',       icon: null                       },
};

function buildEmbedData(url = '') {
  if (!url?.trim()) return null;
  const u = url.trim();
  const spotify = u.match(/open\.spotify\.com\/(track|album|playlist|episode|artist)\/([A-Za-z0-9]+)/);
  const youtube = u.match(/(?:[?&]v=|youtu\.be\/|\/shorts\/|\/embed\/)([A-Za-z0-9_-]{11})/);
  const sc = u.includes('soundcloud.com/');
  if (spotify) return { type: 'spotify', src: `https://open.spotify.com/embed/${spotify[1]}/${spotify[2]}?utm_source=generator&theme=0` };
  if (youtube) return { type: 'youtube', src: `https://www.youtube-nocookie.com/embed/${youtube[1]}?rel=0&modestbranding=1&playsinline=1` };
  if (sc) return { type: 'soundcloud', src: `https://w.soundcloud.com/player/?url=${encodeURIComponent(u)}&color=%23f0ede8&auto_play=false&hide_related=true&show_comments=false&show_user=true` };
  return null;
}
function detectEmbedService(url = '') {
  if (url.includes('spotify.com')) return 'spotify';
  if (url.includes('youtu')) return 'youtube';
  if (url.includes('soundcloud.com')) return 'soundcloud';
  return null;
}

function EmbedPlayer({ url }) {
  const data = buildEmbedData(url);
  if (!data) return <p style={{ fontFamily: F, fontSize: '10px', opacity: 0.3, padding: '8px 0' }}>Paste a Spotify, YouTube, or SoundCloud URL</p>;
  if (data.type === 'youtube') return (
    <div style={{ position: 'relative', width: '100%', paddingTop: '56.25%' }}>
      <iframe src={data.src} title="YouTube" frameBorder="0" allowFullScreen
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }} />
    </div>
  );
  if (data.type === 'spotify') return (
    <iframe src={data.src} width="100%" height="152" frameBorder="0" title="Spotify"
      allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
      loading="lazy" style={{ display: 'block' }} />
  );
  return <iframe width="100%" height="166" frameBorder="0" title="SoundCloud" src={data.src} style={{ display: 'block' }} />;
}

function uid() { return Math.random().toString(36).slice(2, 8); }

function migrateEmbedUrl(buttons, embedUrl) {
  if (!embedUrl?.trim()) return buttons;
  const already = buttons.some(b => b.type === 'embed' && b.url === embedUrl.trim());
  if (already) return buttons;
  return [...buttons, { id: uid(), type: 'embed', url: embedUrl.trim(), label: '' }];
}

/* ─── Design tokens (matches AdminDashboard) ─── */
const D = {
  bg: '#080808', surface: '#0d0d0d', surface2: '#111', surface3: '#161616',
  border: 'rgba(255,255,255,0.07)', borderHi: 'rgba(255,255,255,0.18)',
  text: '#f0ede8', muted: 'rgba(240,237,232,0.4)',
  green: 'rgba(74,222,128,0.9)', red: 'rgba(248,113,113,0.9)',
  radius: '8px', radiusSm: '6px',
  shadow: '0 1px 3px rgba(0,0,0,0.4), 0 4px 12px rgba(0,0,0,0.3)',
};

/* ─── Shared input styles ─── */
const inputStyle = {
  width: '100%', boxSizing: 'border-box', background: D.surface2,
  border: `1px solid ${D.border}`, color: D.text,
  fontFamily: F, fontSize: '15px',
  padding: '11px 14px', outline: 'none', borderRadius: D.radiusSm, WebkitAppearance: 'none',
  transition: 'border-color 0.15s, box-shadow 0.15s',
};
function focusBorder(e) { e.target.style.borderColor = 'rgba(74,222,128,0.5)'; e.target.style.boxShadow = '0 0 0 3px rgba(74,222,128,0.08)'; }
function blurBorder(e)  { e.target.style.borderColor = D.border; e.target.style.boxShadow = 'none'; }

function FieldLabel({ children }) {
  return <p style={{ fontFamily: F, fontSize: '10px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: D.muted, marginBottom: '8px' }}>{children}</p>;
}

function ActionBtn({ onClick, children, danger = false, disabled = false }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      display: 'block', width: '100%', padding: '13px 20px', minHeight: '46px',
      border: `1px solid ${danger ? 'rgba(248,113,113,0.4)' : D.borderHi}`,
      background: danger ? 'rgba(248,113,113,0.06)' : 'rgba(255,255,255,0.04)',
      color: danger ? D.red : D.text,
      fontFamily: F, fontSize: '13px', fontWeight: 600, letterSpacing: '0.04em',
      borderRadius: D.radiusSm, cursor: disabled ? 'default' : 'pointer',
      transition: 'background 0.15s, border-color 0.15s', opacity: disabled ? 0.4 : 1,
      boxSizing: 'border-box', WebkitTapHighlightColor: 'transparent',
    }}
      onMouseOver={e => { if (!disabled) e.currentTarget.style.background = danger ? 'rgba(248,113,113,0.12)' : 'rgba(255,255,255,0.08)'; }}
      onMouseOut={e => { if (!disabled) e.currentTarget.style.background = danger ? 'rgba(248,113,113,0.06)' : 'rgba(255,255,255,0.04)'; }}>
      {children}
    </button>
  );
}

/* ══════════════════════════════════════════════════════════════
   Section — MUST be top-level. If defined inside render(), React
   creates a new component type on every parent re-render, which
   unmounts/remounts children → focused inputs lose focus → keyboard closes.
══════════════════════════════════════════════════════════════ */
function Section({ id, activePanel, setActivePanel, label, icon, children }) {
  const open = activePanel === id;
  return (
    <div style={{
      background: D.surface, border: `1px solid ${open ? D.borderHi : D.border}`,
      borderRadius: D.radius, marginBottom: '8px', overflow: 'hidden',
      boxShadow: open ? D.shadow : 'none', transition: 'border-color 0.2s, box-shadow 0.2s',
    }}>
      <button
        onClick={() => setActivePanel(open ? null : id)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '14px',
          padding: '16px 18px', minHeight: '54px',
          background: open ? D.surface3 : 'transparent',
          border: 'none', color: open ? D.text : D.muted,
          cursor: 'pointer', transition: 'background 0.15s, color 0.15s',
          WebkitTapHighlightColor: 'transparent' }}
        onMouseOver={e => { if (!open) { e.currentTarget.style.background = D.surface2; e.currentTarget.style.color = D.text; } }}
        onMouseOut={e => { if (!open) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = D.muted; } }}
      >
        <span style={{ fontSize: '16px', lineHeight: 1, opacity: open ? 1 : 0.7 }}>{icon}</span>
        <span style={{ fontFamily: F, fontSize: '13px', fontWeight: 600, letterSpacing: '0.02em', flex: 1, textAlign: 'left' }}>{label}</span>
        <span style={{ display: 'inline-block', width: '7px', height: '7px', borderRight: `1.5px solid ${D.borderHi}`, borderBottom: `1.5px solid ${D.borderHi}`, transform: open ? 'rotate(225deg) translateY(-2px)' : 'rotate(45deg)', transition: 'transform 0.2s', marginRight: '2px' }} />
      </button>
      {open && (
        <div style={{ padding: '0 18px 20px', borderTop: `1px solid ${D.border}`, paddingTop: '16px' }}>
          {children}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   BioEditor — top-level, local state for typing.
   Parent state only updated on blur → zero parent re-renders while typing.
══════════════════════════════════════════════════════════════ */
function BioEditor({ initialBio, onSave, saveStatus }) {
  const [local, setLocal] = useState(initialBio);
  // Sync if parent resets (e.g. after load)
  const prev = useRef(initialBio);
  if (initialBio !== prev.current) { prev.current = initialBio; setLocal(initialBio); }

  return (
    <>
      <textarea
        value={local}
        onChange={e => setLocal(e.target.value)}
        onBlur={() => onSave(local, false)}
        placeholder="Write a short bio..."
        rows={5}
        style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6, width: '100%', boxSizing: 'border-box' }}
        onFocus={focusBorder}
      />
      <div style={{ marginTop: '12px' }}>
        <ActionBtn onClick={() => onSave(local, true)} disabled={saveStatus === 'saving'}>
          {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Saved' : 'Save Bio'}
        </ActionBtn>
      </div>
    </>
  );
}

/* ══════════════════════════════════════════════════════════════
   TypeToggle
══════════════════════════════════════════════════════════════ */
function TypeToggle({ value, onChange }) {
  const types = [['link', '→ Link'], ['locked', 'Locked'], ['embed', '▶ Embed']];
  return (
    <div style={{ display: 'flex', border: '1px solid rgba(240,237,232,0.2)', marginBottom: '12px' }}>
      {types.map(([t, lbl], i) => (
        <button key={t} onClick={() => onChange(t)} style={{
          flex: 1, padding: '9px 4px', background: value === t ? 'rgba(240,237,232,0.1)' : 'transparent',
          border: 'none', color: '#f0ede8', fontFamily: F, fontSize: '8px', fontWeight: 700,
          letterSpacing: '0.15em', textTransform: 'uppercase', cursor: 'pointer',
          borderRight: i < types.length - 1 ? '1px solid rgba(240,237,232,0.2)' : 'none',
          opacity: value === t ? 1 : 0.35, transition: 'background 0.15s, opacity 0.15s',
        }}>{lbl}</button>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   ButtonItemCard — top-level, local state for all text fields.
   Flushes to parent only on blur → typing never re-renders parent.
══════════════════════════════════════════════════════════════ */
function ButtonItemCard({ item, index, isActive, dragHandlers, onFlush, onRemove, onChangeType, artistId }) {
  const [localLabel,    setLocalLabel]    = useState(item.label    || '');
  const [localUrl,      setLocalUrl]      = useState(item.url      || '');
  const [localPassword, setLocalPassword] = useState(item.password || '');
  const [localImage,    setLocalImage]    = useState(item.image    || '');
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [imgUploading,  setImgUploading]  = useState(false);
  const [imgProgress,   setImgProgress]   = useState(0);
  const imgFileRef = useRef(null);

  // Sync if item replaced from outside (e.g. type change resets fields)
  const prevId = useRef(item.id);
  const prevType = useRef(item.type);
  if (item.id !== prevId.current || item.type !== prevType.current) {
    prevId.current = item.id;
    prevType.current = item.type;
    setLocalLabel(item.label || '');
    setLocalUrl(item.url || '');
    setLocalPassword(item.password || '');
    setLocalImage(item.image || '');
  }

  function flush(overrides = {}) {
    onFlush(item.id, { label: localLabel, url: localUrl, password: localPassword, image: localImage, ...overrides });
  }

  async function handleImageFile(file) {
    if (!file) return;
    if (CLOUDINARY_CLOUD_NAME === 'YOUR_CLOUD_NAME') { alert('Cloudinary not configured.'); return; }
    setImgUploading(true); setImgProgress(0);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
      formData.append('folder', `yen-sound/artists/${artistId}/buttons`);
      const url = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`);
        xhr.upload.onprogress = e => { if (e.lengthComputable) setImgProgress(Math.round((e.loaded / e.total) * 100)); };
        xhr.onload = () => {
          if (xhr.status === 200) {
            const d = JSON.parse(xhr.responseText);
            resolve(d.secure_url.replace('/upload/', '/upload/c_fill,ar_4:3,f_auto,q_auto/'));
          } else reject(new Error('Upload failed'));
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(formData);
      });
      setLocalImage(url);
      onFlush(item.id, { image: url });
    } catch (err) { alert('Upload failed: ' + err.message); }
    setImgUploading(false);
  }

  const h = dragHandlers(index);

  return (
    <div style={{
      marginBottom: '10px',
      border: `1px solid ${isActive ? 'rgba(240,237,232,0.5)' : 'rgba(240,237,232,0.1)'}`,
      background: isActive ? '#111' : '#080808',
      transform: isActive ? 'scale(1.01)' : 'scale(1)',
      transition: 'border-color 0.12s, background 0.12s, transform 0.12s',
    }}>
      {/* drag handle — only this strip has drag+touch handlers */}
      <div ref={h.ref} draggable={h.draggable}
        onDragStart={h.onDragStart} onDragOver={h.onDragOver} onDragEnd={h.onDragEnd}
        onTouchStart={h.onTouchStart} onTouchMove={h.onTouchMove} onTouchEnd={h.onTouchEnd}
        style={{ display: 'flex', alignItems: 'center', padding: '10px 14px 0', opacity: 0.3, cursor: 'grab', userSelect: 'none', touchAction: 'none' }}>
        <span style={{ fontFamily: F, fontSize: '9px', letterSpacing: '0.2em' }}>⠿ Hold to drag</span>
        <span style={{ fontFamily: F, fontSize: '9px', marginLeft: 'auto' }}>#{index + 1}</span>
      </div>

      {/* fields — touch events stopped so they never bubble to drag handle */}
      <div style={{ padding: '10px 14px 14px' }}
        onTouchStart={e => e.stopPropagation()}
        onTouchMove={e => e.stopPropagation()}
        onTouchEnd={e => e.stopPropagation()}>

        <TypeToggle value={item.type} onChange={t => onChangeType(item.id, t)} />

        {item.type === 'link' && (
          <>
            <FieldLabel>Label</FieldLabel>
            <div style={{ marginBottom: '10px' }}>
              <input value={localLabel} onChange={e => setLocalLabel(e.target.value)}
                onBlur={() => flush()} placeholder="e.g. BANDCAMP"
                style={inputStyle} onFocus={focusBorder} />
            </div>
            <FieldLabel>URL</FieldLabel>
            <div style={{ marginBottom: '10px' }}>
              <input value={localUrl} onChange={e => setLocalUrl(e.target.value)}
                onBlur={() => flush()} placeholder="https://..."
                style={inputStyle} onFocus={focusBorder} />
            </div>

            {/* Image section */}
            <FieldLabel>Cover Image (optional · 4:3)</FieldLabel>
            {localImage ? (
              <div style={{ marginBottom: '10px', position: 'relative' }}>
                <div style={{ width: '100%', aspectRatio: '4/3', overflow: 'hidden', background: '#111', marginBottom: '6px' }}>
                  <img src={localImage} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => imgFileRef.current?.click()}
                    style={{ flex: 1, padding: '8px', background: 'transparent', border: '1px solid rgba(240,237,232,0.2)', color: '#f0ede8', fontFamily: F, fontSize: '9px', letterSpacing: '0.15em', textTransform: 'uppercase', cursor: 'pointer' }}>
                    Replace
                  </button>
                  <button onClick={() => { setLocalImage(''); onFlush(item.id, { image: '' }); }}
                    style={{ padding: '8px 14px', background: 'transparent', border: '1px solid rgba(220,80,80,0.4)', color: 'rgba(220,80,80,0.8)', fontFamily: F, fontSize: '9px', letterSpacing: '0.15em', textTransform: 'uppercase', cursor: 'pointer' }}>
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ marginBottom: '10px' }}>
                {/* Upload button */}
                <div onClick={() => imgFileRef.current?.click()}
                  style={{ width: '100%', aspectRatio: '4/3', background: '#0a0a0a', border: '1px dashed rgba(240,237,232,0.2)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer', marginBottom: '8px', position: 'relative' }}>
                  {imgUploading ? (
                    <>
                      <div style={{ width: '80px', height: '2px', background: '#222' }}>
                        <div style={{ width: `${imgProgress}%`, height: '100%', background: '#f0ede8', transition: 'width 0.2s' }} />
                      </div>
                      <span style={{ fontFamily: F, fontSize: '8px', opacity: 0.4, letterSpacing: '0.1em' }}>{imgProgress}%</span>
                    </>
                  ) : (
                    <>
                      <span style={{ fontFamily: F, fontSize: '18px', opacity: 0.2 }}>+</span>
                      <span style={{ fontFamily: F, fontSize: '8px', letterSpacing: '0.2em', textTransform: 'uppercase', opacity: 0.3 }}>Upload Image</span>
                    </>
                  )}
                </div>
                {/* URL paste */}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input value={localImage} onChange={e => setLocalImage(e.target.value)}
                    onBlur={() => flush()} placeholder="or paste image URL..."
                    style={{ ...inputStyle, fontSize: '13px' }} onFocus={focusBorder} />
                </div>
              </div>
            )}
            <input ref={imgFileRef} type="file" accept="image/*" style={{ display: 'none' }}
              onChange={e => handleImageFile(e.target.files?.[0])} />

            <button onClick={() => setShowIconPicker(v => !v)}
              style={{ background: 'none', border: 'none', color: '#f0ede8', fontFamily: F, fontSize: '9px', letterSpacing: '0.2em', textTransform: 'uppercase', opacity: 0.3, cursor: 'pointer', padding: 0, marginBottom: showIconPicker ? '10px' : 0 }}>
              {showIconPicker ? '- hide icon' : '+ add icon (optional)'}
            </button>
            {showIconPicker && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
                {ICON_OPTIONS.map(opt => (
                  <button key={opt.value} onClick={() => { onFlush(item.id, { icon: opt.value }); }}
                    style={{ padding: '6px 10px', background: item.icon === opt.value ? 'rgba(240,237,232,0.15)' : 'transparent', border: `1px solid ${item.icon === opt.value ? 'rgba(240,237,232,0.6)' : 'rgba(240,237,232,0.2)'}`, color: '#f0ede8', fontFamily: F, fontSize: '9px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', letterSpacing: '0.1em' }}>
                    {opt.value && ICON_RENDER[opt.value] ? ICON_RENDER[opt.value] : null}
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {item.type === 'locked' && (
          <>
            <FieldLabel>Button label (shown publicly)</FieldLabel>
            <div style={{ marginBottom: '10px' }}>
              <input value={localLabel} onChange={e => setLocalLabel(e.target.value)}
                onBlur={() => flush()} placeholder="e.g. EXCLUSIVE LINK"
                style={inputStyle} onFocus={focusBorder} />
            </div>
            <FieldLabel>Password</FieldLabel>
            <div style={{ marginBottom: '10px' }}>
              <input value={localPassword} onChange={e => setLocalPassword(e.target.value)}
                onBlur={() => flush()} placeholder="Enter password"
                style={inputStyle} onFocus={focusBorder} />
            </div>
            <FieldLabel>URL revealed after correct password</FieldLabel>
            <div style={{ marginBottom: '8px' }}>
              <input value={localUrl} onChange={e => setLocalUrl(e.target.value)}
                onBlur={() => flush()} placeholder="https://..."
                style={inputStyle} onFocus={focusBorder} />
            </div>
            <p style={{ fontFamily: F, fontSize: '8px', opacity: 0.3, letterSpacing: '0.08em', lineHeight: 1.6 }}>
              Visitors see a lock button. Correct password opens the URL.
            </p>
          </>
        )}

        {item.type === 'embed' && (
          <>
            <FieldLabel>Label (optional)</FieldLabel>
            <div style={{ marginBottom: '10px' }}>
              <input value={localLabel} onChange={e => setLocalLabel(e.target.value)}
                onBlur={() => flush()} placeholder="e.g. Latest Track"
                style={inputStyle} onFocus={focusBorder} />
            </div>
            <FieldLabel>Spotify, YouTube, or SoundCloud URL</FieldLabel>
            <input value={localUrl} onChange={e => setLocalUrl(e.target.value)}
              onBlur={() => flush()} placeholder="https://open.spotify.com/track/..."
              style={inputStyle} onFocus={focusBorder} />
            {item.url && buildEmbedData(item.url) && (
              <div style={{ marginTop: '12px', opacity: 0.65 }}>
                <EmbedPlayer url={item.url} />
              </div>
            )}
            {item.url && !buildEmbedData(item.url) && (
              <p style={{ fontFamily: F, fontSize: '9px', opacity: 0.35, marginTop: '6px', letterSpacing: '0.1em' }}>
                URL not recognised — try a direct Spotify/YouTube/SoundCloud link
              </p>
            )}
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
          <button onClick={() => onRemove(item.id)}
            style={{ background: 'transparent', border: '1px solid rgba(220,80,80,0.4)', color: 'rgba(220,80,80,0.8)', fontFamily: F, fontSize: '9px', letterSpacing: '0.2em', textTransform: 'uppercase', padding: '6px 12px', cursor: 'pointer' }}>
            Remove
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   Touch-aware sortable hook
══════════════════════════════════════════════════════════════ */
function useTouchSort(setList) {
  const dragIdx = useRef(null);
  const longPressTimer = useRef(null);
  const isDragging = useRef(false);
  const rowRefs = useRef([]);
  const startY = useRef(0);
  const [activeIdx, setActiveIdx] = useState(null);

  function getRowAtY(y) {
    let closest = null, closestDist = Infinity;
    rowRefs.current.forEach((el, i) => {
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const mid = rect.top + rect.height / 2;
      const dist = Math.abs(y - mid);
      if (dist < closestDist) { closestDist = dist; closest = i; }
    });
    return closest;
  }

  function moveItem(from, to) {
    if (from === to || from === null || to === null) return;
    setList(prev => { const a = [...prev]; const [m] = a.splice(from, 1); a.splice(to, 0, m); return a; });
    dragIdx.current = to; setActiveIdx(to);
  }

  const handlers = useCallback((index) => ({
    ref: el => { rowRefs.current[index] = el; },
    draggable: true,
    onDragStart: () => { dragIdx.current = index; setActiveIdx(index); },
    onDragOver: e => { e.preventDefault(); if (dragIdx.current !== null && dragIdx.current !== index) moveItem(dragIdx.current, index); },
    onDragEnd: () => { dragIdx.current = null; setActiveIdx(null); },
    onTouchStart: e => {
      startY.current = e.touches[0].clientY;
      longPressTimer.current = setTimeout(() => {
        isDragging.current = true; dragIdx.current = index; setActiveIdx(index);
        if (navigator.vibrate) navigator.vibrate(30);
      }, 400);
    },
    onTouchMove: e => {
      if (!isDragging.current) { if (Math.abs(e.touches[0].clientY - startY.current) > 8) clearTimeout(longPressTimer.current); return; }
      e.preventDefault();
      const target = getRowAtY(e.touches[0].clientY);
      if (target !== null && dragIdx.current !== null && target !== dragIdx.current) moveItem(dragIdx.current, target);
    },
    onTouchEnd: () => { clearTimeout(longPressTimer.current); isDragging.current = false; dragIdx.current = null; setActiveIdx(null); },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  return { handlers, activeIdx };
}

/* ══════════════════════════════════════════════════════════════
   OrderRow
══════════════════════════════════════════════════════════════ */
function OrderRow({ itemKey, index, customButtons, socials, dragHandlers, isActive }) {
  const platform = PLATFORM_META[itemKey];
  let label, icon, dimmed = false;

  if (platform) {
    label = platform.label; icon = platform.icon;
    dimmed = itemKey !== 'press' && !(socials[itemKey] && socials[itemKey] !== 'PLACEHOLDER');
  } else {
    const item = customButtons.find(b => b.id === itemKey);
    if (!item) return null;
    if (item.type === 'embed') { const svc = detectEmbedService(item.url); label = item.label || (svc ? `${svc} embed` : 'Embed'); icon = <span style={{ fontSize: '11px', opacity: 0.5 }}>▶</span>; }
    else if (item.type === 'locked') { label = item.label || 'Locked link'; icon = <span style={{ fontSize: '10px', opacity: 0.5, fontFamily: F }}>lock</span>; }
    else { label = item.label || 'Link'; icon = item.icon ? (ICON_RENDER[item.icon] || null) : null; }
  }

  const h = dragHandlers(index);
  return (
    <div ref={h.ref} draggable={h.draggable}
      onDragStart={h.onDragStart} onDragOver={h.onDragOver} onDragEnd={h.onDragEnd}
      onTouchStart={h.onTouchStart} onTouchMove={h.onTouchMove} onTouchEnd={h.onTouchEnd}
      style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px', marginBottom: '6px', userSelect: 'none', touchAction: 'none', border: `1px solid ${isActive ? 'rgba(240,237,232,0.5)' : 'rgba(240,237,232,0.1)'}`, background: isActive ? '#111' : '#050505', cursor: 'grab', opacity: dimmed ? 0.3 : 1, transform: isActive ? 'scale(1.015)' : 'scale(1)', transition: 'border-color 0.12s, background 0.12s, transform 0.12s' }}>
      <span style={{ fontFamily: F, fontSize: '14px', opacity: 0.3, lineHeight: 1 }}>⠿</span>
      <span style={{ display: 'flex', alignItems: 'center', minWidth: '18px' }}>{icon}</span>
      <span style={{ fontFamily: F, fontSize: '10px', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', flex: 1 }}>{label}</span>
      {isActive && <span style={{ fontFamily: F, fontSize: '8px', letterSpacing: '0.12em', opacity: 0.5, textTransform: 'uppercase' }}>moving</span>}
      {!isActive && dimmed && <span style={{ fontFamily: F, fontSize: '8px', letterSpacing: '0.15em', opacity: 0.4, textTransform: 'uppercase' }}>Not set</span>}
      {!isActive && !platform && !dimmed && <span style={{ fontFamily: F, fontSize: '8px', letterSpacing: '0.1em', opacity: 0.25, textTransform: 'uppercase' }}>custom</span>}
      <span style={{ fontFamily: F, fontSize: '9px', opacity: 0.2 }}>#{index + 1}</span>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   MiniPreview — top-level, receives a frozen snapshot
══════════════════════════════════════════════════════════════ */
function MiniPreview({ theme, bio, photoUrl, buttonOrder, customButtons, socials, artistDisplayName, currentPhoto, merchItems, showMerch }) {
  const { bg, fg: fgColor } = themeColors(theme);
  const isLight = bg !== '#000000' && bg !== '#0f1117' && bg !== '#0d0305' && bg !== '#050d07' && bg !== '#0e0812' && bg !== '#080c1a';
  const fg = fgColor;
  const muted = `rgba(${isLight ? '10,10,10' : '240,237,232'},0.4)`;
  const border = isLight ? 'rgba(10,10,10,0.12)' : 'rgba(240,237,232,0.1)';
  const btnBorder = isLight ? 'rgba(10,10,10,0.6)' : 'rgba(240,237,232,0.7)';
  const logoFilter = isLight ? 'invert(1)' : 'none';
  const PF = F;

  const orderedItems = buttonOrder.map(key => {
    if (PLATFORM_META[key]) {
      if (key === 'press') return { key: 'press', kind: 'platform', label: 'PRESS' };
      const url = socials[key];
      if (!url || url === 'PLACEHOLDER') return null;
      return { key, kind: 'platform', label: PLATFORM_META[key].label };
    }
    const item = customButtons.find(b => b.id === key);
    if (!item) return null;
    if (item.type === 'embed') return { key, kind: 'embed', url: item.url, label: item.label };
    if (item.type === 'locked') return { key, kind: 'locked', label: item.label || 'Locked' };
    if (item.type === 'link' && item.label && item.url) return { key, kind: 'link', label: item.label, image: item.image || null };
    return null;
  }).filter(Boolean);

  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <div style={{ width: '220px', flexShrink: 0, border: '6px solid #1a1a1a', borderRadius: '28px', overflow: 'hidden', boxShadow: '0 0 0 1px rgba(255,255,255,0.06), 0 20px 60px rgba(0,0,0,0.7)', background: bg, transition: 'background 0.3s', position: 'relative' }}>
        <div style={{ position: 'absolute', top: '8px', left: '50%', transform: 'translateX(-50%)', width: '48px', height: '10px', background: '#1a1a1a', borderRadius: '8px', zIndex: 10 }} />
        <div style={{ height: '420px', overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch', background: bg }}>
          <div style={{ overflow: 'hidden', borderBottom: `1px solid ${border}`, padding: '5px 0', marginTop: '24px' }}>
            <div style={{ display: 'inline-flex', animation: 'marquee 18s linear infinite', whiteSpace: 'nowrap' }}>
              {Array(4).fill('YEN SOUND ®   ').map((t, i) => <span key={i} style={{ fontFamily: PF, fontSize: '5px', fontWeight: 700, letterSpacing: '0.3em', textTransform: 'uppercase', color: muted, paddingRight: '20px' }}>{t}</span>)}
            </div>
          </div>
          {currentPhoto
            ? <div style={{ width: '100%', aspectRatio: '1', overflow: 'hidden' }}><img src={currentPhoto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', display: 'block' }} /></div>
            : <div style={{ width: '100%', aspectRatio: '1', background: '#111', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span style={{ fontFamily: PF, fontSize: '7px', color: muted, letterSpacing: '0.15em' }}>No Photo</span></div>
          }
          <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0' }}>
            <img src="/spinning yen logo white.gif" alt="" style={{ width: '14px', height: '14px', opacity: 0.4, filter: logoFilter }} />
          </div>
          <div style={{ padding: '6px 10px 0', textAlign: 'center' }}>
            <p style={{ fontFamily: PF, fontSize: '8px', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: fg, marginBottom: bio ? '5px' : 0, lineHeight: 1.3 }}>{artistDisplayName}</p>
            {bio && <p style={{ fontFamily: PF, fontSize: '6px', color: muted, lineHeight: 1.6, letterSpacing: '0.04em' }}>{bio.slice(0, 80)}{bio.length > 80 ? '…' : ''}</p>}
          </div>
          <p style={{ fontFamily: PF, fontSize: '5px', letterSpacing: '0.25em', textTransform: 'uppercase', color: muted, textAlign: 'center', padding: '8px 0 5px' }}>Choose music service</p>
          <div style={{ padding: '0 8px 16px' }}>
            {orderedItems.length === 0 && <p style={{ fontFamily: PF, fontSize: '6px', color: muted, textAlign: 'center', padding: '6px 0' }}>No links set</p>}
            {orderedItems.map((item, i) => {
              if (item.kind === 'embed') {
                const ed = buildEmbedData(item.url);
                return (
                  <div key={i} style={{ marginBottom: '5px', border: `1px solid ${border}`, overflow: 'hidden' }}>
                    {item.label && <p style={{ fontFamily: PF, fontSize: '5px', letterSpacing: '0.15em', textTransform: 'uppercase', color: muted, textAlign: 'center', padding: '4px 0 2px' }}>{item.label}</p>}
                    {ed?.type === 'youtube' && <div style={{ position: 'relative', width: '100%', paddingTop: '56.25%' }}><iframe src={ed.src} title="yt" frameBorder="0" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none', pointerEvents: 'none' }} /></div>}
                    {ed?.type === 'spotify' && <iframe src={ed.src} width="100%" height="60" frameBorder="0" title="sp" style={{ display: 'block', pointerEvents: 'none' }} />}
                    {ed?.type === 'soundcloud' && <iframe src={ed.src} width="100%" height="60" frameBorder="0" title="sc" style={{ display: 'block', pointerEvents: 'none' }} />}
                    {!ed && <div style={{ padding: '8px', textAlign: 'center' }}><span style={{ fontFamily: PF, fontSize: '6px', color: muted }}>▶ {item.label || 'Embed'}</span></div>}
                  </div>
                );
              }
              if (item.kind === 'locked') return (
                <div key={i} style={{ padding: '7px 6px', marginBottom: '4px', border: `1px solid ${btnBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                  <span style={{ fontFamily: PF, fontSize: '6px', opacity: 0.5 }}>lock</span>
                  <span style={{ fontFamily: PF, fontSize: '6px', fontWeight: 700, letterSpacing: '0.2em', color: fg, textTransform: 'uppercase' }}>{item.label}</span>
                </div>
              );
              return (
                <div key={i} style={{ marginBottom: '4px', border: `1px solid ${btnBorder}`, overflow: 'hidden' }}>
                  {item.image && (
                    <div style={{ width: '100%', aspectRatio: '4/3', overflow: 'hidden' }}>
                      <img src={item.image} alt={item.label} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    </div>
                  )}
                  <div style={{ padding: '7px 6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontFamily: PF, fontSize: '6px', fontWeight: 700, letterSpacing: '0.2em', color: fg, textTransform: 'uppercase' }}>{item.label}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* merch preview */}
          {showMerch && merchItems && merchItems.length > 0 && (
            <div style={{ padding: '8px 8px 12px', borderTop: `1px solid ${border}` }}>
              <p style={{ fontFamily: PF, fontSize: '5px', fontWeight: 700, letterSpacing: '0.3em', textTransform: 'uppercase', color: muted, marginBottom: '8px' }}>Merch</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '4px' }}>
                {merchItems.slice(0, 3).map((item, i) => {
                  const isLocked = item.locked_until && new Date(item.locked_until + 'T00:00:00+02:00') > new Date();
                  return (
                    <div key={i}>
                      <div style={{ aspectRatio: '1', background: '#000', overflow: 'hidden', marginBottom: '3px' }}>
                        {item.image_url && <img src={item.image_url} alt='' style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', filter: isLocked ? 'grayscale(1) brightness(0.3)' : 'brightness(0.85)', pointerEvents: 'none' }} />}
                      </div>
                      <p style={{ fontFamily: PF, fontSize: '4.5px', fontWeight: 600, color: fg, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════ */
/* ══════════════════════════════════════════════════════════════
   EPK EDITOR — full screen overlay
══════════════════════════════════════════════════════════════ */
const EPK_LABELS = {
  en: {
    title: 'Press Kit', subtitle: 'Electronic Press Kit',
    photo: 'Press Photo', photoHint: 'Tap photo to upload, or paste a URL',
    tagline: 'Tagline / Genre', taglinePlaceholder: 'e.g. Dark Electronic · Tel Aviv',
    bio: 'Biography', bioPlaceholder: 'Write a short bio for the press kit...',
    links: 'Social Links',
    featured: 'Videos (up to 4 YouTube URLs)',
    featuredPlaceholder: 'https://youtube.com/watch?v=...',
    featuredLabel: 'FEATURED TRACK',
    theme: 'EPK Theme',
    releases: 'Releases to include',
    all: 'All', none: 'None',
    preview: 'Preview PDF →', close: 'Close',
    dir: 'ltr', align: 'left',
  },
  he: {
    title: 'תיק עיתונות', subtitle: 'Electronic Press Kit',
    photo: 'תמונת פרס', photoHint: 'לחץ על התמונה להעלאה, או הדבק URL',
    tagline: 'כותרת משנית / ז\u05f3אנר', taglinePlaceholder: 'למשל: אלקטרוני · תל אביב',
    bio: 'ביוגרפיה', bioPlaceholder: 'כתוב ביו קצר לתיק העיתונות...',
    links: 'לינקים לרשתות',
    featured: 'סרטונים (עד 4 לינקי YouTube)',
    featuredPlaceholder: 'https://youtube.com/watch?v=...',
    featuredLabel: 'ספוטלייט',
    theme: 'ערכת נושא',
    releases: 'ריליזים לכלול',
    all: 'הכל', none: 'ניקוי',
    preview: 'תצוגה מקדימה PDF →', close: 'סגור',
    dir: 'rtl', align: 'right',
  },
};

function EpkEditor({ data, setData, onClose, onGenerate, uploadPhoto, D, F }) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const photoRef = useRef(null);
  const lang = data.language || 'en';
  const isHe = lang === 'he';
  const L = EPK_LABELS[lang];

  function update(field, value) {
    setData(prev => ({ ...prev, [field]: value }));
  }

  function toggleRelease(key) {
    setData(prev => {
      const sel = prev.selectedReleases;
      return {
        ...prev,
        selectedReleases: sel.includes(key)
          ? sel.filter(k => k !== key)
          : [...sel, key],
      };
    });
  }

  async function handlePhotoFile(file) {
    if (!file) return;
    setUploading(true); setUploadProgress(0);
    // preview immediately
    const local = URL.createObjectURL(file);
    update('pressPhoto', local);
    try {
      const url = await uploadPhoto(file, p => setUploadProgress(p));
      if (url) update('pressPhoto', url);
    } catch { /* keep local preview */ }
    setUploading(false);
  }


  const inputS = {
    width: '100%', boxSizing: 'border-box', background: D.surface2,
    border: '1px solid ' + D.border, color: D.text, fontFamily: F,
    fontSize: '13px', padding: '10px 14px', outline: 'none',
    borderRadius: D.radiusSm, transition: 'border-color 0.15s',
  };
  const labelS = {
    fontFamily: F, fontSize: '10px', fontWeight: 600, letterSpacing: '0.06em',
    textTransform: 'uppercase', color: D.muted, display: 'block', marginBottom: '8px',
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: D.bg, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>

      {/* header */}
      <div style={{ position: 'sticky', top: 0, zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', paddingTop: 'calc(60px + env(safe-area-inset-top))', background: D.bg, borderBottom: '1px solid ' + D.border }}>
        <div>
          <p style={{ fontFamily: F, fontSize: '13px', fontWeight: 700, color: D.text, letterSpacing: '0.04em' }}>{L.title}</p>
          <p style={{ fontFamily: F, fontSize: '10px', color: D.muted, marginTop: '2px' }}>{L.subtitle}</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {/* language toggle */}
          <div style={{ display: 'flex', border: '1px solid ' + D.border, borderRadius: D.radiusSm, overflow: 'hidden' }}>
            {['en','he'].map(l => (
              <button key={l} onClick={() => update('language', l)} style={{
                fontFamily: F, fontSize: '11px', fontWeight: 600, padding: '8px 14px',
                background: lang === l ? D.borderHi : 'transparent',
                color: lang === l ? D.text : D.muted,
                border: 'none', cursor: 'pointer', letterSpacing: '0.04em',
              }}>
                {l === 'en' ? 'EN' : 'עב'}
              </button>
            ))}
          </div>
          <button onClick={onGenerate}
            style={{ background: D.text, color: D.bg, border: 'none', fontFamily: F, fontSize: '12px', fontWeight: 700, letterSpacing: '0.04em', padding: '10px 20px', borderRadius: D.radiusSm, cursor: 'pointer' }}>
            {L.preview}
          </button>
          <button onClick={onClose}
            style={{ background: 'transparent', border: '1px solid ' + D.border, color: D.muted, fontFamily: F, fontSize: '11px', padding: '10px 16px', borderRadius: D.radiusSm, cursor: 'pointer' }}>
            {L.close}
          </button>
        </div>
      </div>

      <div style={{ maxWidth: '640px', margin: '0 auto', padding: '28px 20px 80px', direction: L.dir, textAlign: L.align }}>

        {/* Press Photo */}
        <div style={{ marginBottom: '24px' }}>
          <span style={labelS}>{L.photo}</span>
          <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
            <div style={{ width: '90px', height: '90px', flexShrink: 0, border: '1px solid ' + D.border, borderRadius: D.radiusSm, overflow: 'hidden', position: 'relative', cursor: 'pointer', background: D.surface2 }}
              onClick={() => photoRef.current?.click()}>
              {data.pressPhoto
                ? <img src={data.pressPhoto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', display: 'block' }} />
                : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: F, fontSize: '9px', color: D.muted }}>+ Photo</div>
              }
              {uploading && (
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: F, fontSize: '9px', color: D.text }}>{uploadProgress}%</div>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <input ref={photoRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handlePhotoFile(e.target.files?.[0])} />
              <input value={data.pressPhoto} onChange={e => update('pressPhoto', e.target.value)} placeholder="Or paste image URL..."
                style={inputS}
                onFocus={e => { e.target.style.borderColor = 'rgba(74,222,128,0.5)'; e.target.style.boxShadow = '0 0 0 3px rgba(74,222,128,0.08)'; }}
                onBlur={e => { e.target.style.borderColor = D.border; e.target.style.boxShadow = 'none'; }}
              />
              <p style={{ fontFamily: F, fontSize: '10px', color: D.muted, marginTop: '6px' }}>{L.photoHint}</p>
            </div>
          </div>
        </div>

        {/* Tagline */}
        <div style={{ marginBottom: '20px' }}>
          <span style={labelS}>{L.tagline}</span>
          <input value={data.tagline} onChange={e => update('tagline', e.target.value)}
            placeholder={L.taglinePlaceholder}
            style={inputS}
            onFocus={e => { e.target.style.borderColor = 'rgba(74,222,128,0.5)'; e.target.style.boxShadow = '0 0 0 3px rgba(74,222,128,0.08)'; }}
            onBlur={e => { e.target.style.borderColor = D.border; e.target.style.boxShadow = 'none'; }}
          />
        </div>

        {/* Bio */}
        <div style={{ marginBottom: '24px' }}>
          <span style={labelS}>{L.bio}</span>
          <textarea value={data.bio} onChange={e => update('bio', e.target.value)}
            placeholder={L.bioPlaceholder}
            rows={5}
            style={{ ...inputS, resize: 'vertical', lineHeight: 1.7, direction: isHe ? 'rtl' : 'ltr' }}
            onFocus={e => { e.target.style.borderColor = 'rgba(74,222,128,0.5)'; e.target.style.boxShadow = '0 0 0 3px rgba(74,222,128,0.08)'; }}
            onBlur={e => { e.target.style.borderColor = D.border; e.target.style.boxShadow = 'none'; }}
          />
        </div>

        {/* Social Links */}
        <div style={{ marginBottom: '24px' }}>
          <span style={labelS}>{L.links}</span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {['spotify','appleMusic','instagram','youtube','tiktok','soundcloud','website'].map(k => (
              <div key={k} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontFamily: F, fontSize: '10px', color: D.muted, width: '90px', flexShrink: 0, textTransform: 'capitalize', letterSpacing: '0.04em' }}>
                  {k === 'appleMusic' ? 'Apple Music' : k.charAt(0).toUpperCase() + k.slice(1)}
                </span>
                <input value={data.socials?.[k] || ''} onChange={e => setData(p => ({ ...p, socials: { ...p.socials, [k]: e.target.value } }))}
                  placeholder="https://..."
                  style={{ ...inputS, flex: 1 }}
                  onFocus={e => { e.target.style.borderColor = 'rgba(74,222,128,0.5)'; e.target.style.boxShadow = '0 0 0 3px rgba(74,222,128,0.08)'; }}
                  onBlur={e => { e.target.style.borderColor = D.border; e.target.style.boxShadow = 'none'; }}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Theme */}
        <div style={{ marginBottom: '24px' }}>
          <span style={labelS}>{L.theme}</span>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '8px' }}>
            {[
              { id: 'dark',     label: 'Void',     bg: '#000', fg: '#f0ede8' },
              { id: 'light',    label: 'Paper',    bg: '#f5f3ef', fg: '#0a0a0a' },
              { id: 'slate',    label: 'Slate',    bg: '#0f1117', fg: '#e8eaf0' },
              { id: 'crimson',  label: 'Crimson',  bg: '#0d0305', fg: '#f5c6c0' },
              { id: 'forest',   label: 'Forest',   bg: '#050d07', fg: '#c8e6c0' },
              { id: 'dusk',     label: 'Dusk',     bg: '#0e0812', fg: '#e8d4f5' },
              { id: 'sand',     label: 'Sand',     bg: '#f2ede4', fg: '#2a1f0a' },
              { id: 'midnight', label: 'Midnight', bg: '#080c1a', fg: '#c8d4f0' },
            ].map(t => (
              <button key={t.id} onClick={() => update('epkTheme', t.id)} style={{
                padding: 0, border: '2px solid ' + ((data.epkTheme || 'dark') === t.id ? 'rgba(74,222,128,0.6)' : D.border),
                borderRadius: D.radiusSm, overflow: 'hidden', cursor: 'pointer', background: 'transparent',
              }}>
                <div style={{ height: '28px', display: 'flex' }}>
                  <div style={{ flex: 1, background: t.bg }} />
                  <div style={{ flex: 1, background: t.fg }} />
                </div>
                <div style={{ padding: '4px', background: D.surface, textAlign: 'center' }}>
                  <span style={{ fontFamily: F, fontSize: '8px', fontWeight: 600, color: (data.epkTheme || 'dark') === t.id ? 'rgba(74,222,128,0.9)' : D.muted, letterSpacing: '0.08em' }}>{t.label}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Featured Videos */}
        <div style={{ marginBottom: '24px' }}>
          <span style={labelS}>{L.featured}</span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {[0,1,2,3].map(i => (
              <input key={i}
                value={(data.featuredVideos || ['','','',''])[i] || ''}
                onChange={e => {
                  const arr = [...(data.featuredVideos || ['','','',''])];
                  arr[i] = e.target.value;
                  update('featuredVideos', arr);
                }}
                placeholder={i === 0 ? L.featuredPlaceholder : 'https://youtube.com/watch?v=...'}
                style={{ ...inputS }}
                onFocus={e => { e.target.style.borderColor = 'rgba(74,222,128,0.5)'; e.target.style.boxShadow = '0 0 0 3px rgba(74,222,128,0.08)'; }}
                onBlur={e => { e.target.style.borderColor = D.border; e.target.style.boxShadow = 'none'; }}
              />
            ))}
          </div>
        </div>

        {/* Releases */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <span style={labelS}>{L.releases}</span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => setData(p => ({ ...p, selectedReleases: p.allReleases.map(r => r.slug || r.title) }))}
                style={{ fontFamily: F, fontSize: '9px', background: 'transparent', border: '1px solid ' + D.border, color: D.muted, padding: '4px 10px', borderRadius: '4px', cursor: 'pointer' }}>{L.all}</button>
              <button onClick={() => setData(p => ({ ...p, selectedReleases: [] }))}
                style={{ fontFamily: F, fontSize: '9px', background: 'transparent', border: '1px solid ' + D.border, color: D.muted, padding: '4px 10px', borderRadius: '4px', cursor: 'pointer' }}>{L.none}</button>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {data.allReleases.map(r => {
              const key = r.slug || r.title;
              const checked = data.selectedReleases.includes(key);
              return (
                <div key={key} onClick={() => toggleRelease(key)}
                  style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', background: checked ? 'rgba(74,222,128,0.05)' : D.surface, border: '1px solid ' + (checked ? 'rgba(74,222,128,0.25)' : D.border), borderRadius: D.radiusSm, cursor: 'pointer', transition: 'all 0.15s' }}>
                  <div style={{ width: '18px', height: '18px', flexShrink: 0, border: '1px solid ' + (checked ? 'rgba(74,222,128,0.6)' : D.borderHi), borderRadius: '4px', background: checked ? 'rgba(74,222,128,0.2)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {checked && <span style={{ color: 'rgba(74,222,128,0.9)', fontSize: '11px', lineHeight: 1 }}>✓</span>}
                  </div>
                  {r.cover && <img src={r.cover} alt="" style={{ width: '36px', height: '36px', objectFit: 'cover', borderRadius: '3px', flexShrink: 0 }} />}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontFamily: F, fontSize: '12px', fontWeight: 600, color: D.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.title}</p>
                    <p style={{ fontFamily: F, fontSize: '10px', color: D.muted, marginTop: '2px' }}>{r.date?.slice(0,4)} · {r.type}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </div>
  );
}

export default function ArtistDashboard() {
  const { artistId } = useParams();
  const [artist, setArtist] = useState(null);
  const [loading, setLoading] = useState(true);

  const [bio, setBio] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [theme, setTheme] = useState('dark');
  const [showMerch, setShowMerch] = useState(true);
  const [previewMerchItems, setPreviewMerchItems] = useState([]);
  const [themeStatus, setThemeStatus] = useState('idle');
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoProgress, setPhotoProgress] = useState(0);
  const [photoStatus, setPhotoStatus] = useState('idle');
  const photoFileRef = useRef(null);
  const [customButtons, setCustomButtons] = useState([]);
  const [buttonOrder, setButtonOrder] = useState(DEFAULT_ORDER);
  const [activePanel, setActivePanel] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showEpk, setShowEpk] = useState(false);
  const [epkData, setEpkData] = useState(null);
  const [showFormSigner, setShowFormSigner] = useState(false);

  const [bioStatus, setBioStatus] = useState('idle');
  const [btnStatus, setBtnStatus] = useState('idle');
  const [resetStatus, setResetStatus] = useState('idle');
  const [saveError, setSaveError] = useState(null);

  // Frozen snapshot for preview — only updates on explicit refresh, never on keystrokes
  const [previewSnapshot, setPreviewSnapshot] = useState(null);

  const { handlers: customHandlers, activeIdx: customActiveIdx } = useTouchSort(setCustomButtons);
  const { handlers: orderHandlers, activeIdx: orderActiveIdx } = useTouchSort(setButtonOrder);

  const rosterArtist = artist?.slug ? roster.find(r => r.slug === artist.slug) : null;
  const socials = rosterArtist?.socials || {};

  // Release titles for the distribution form's "שם התקליט" suggestions
  function artistReleaseTitles() {
    if (!artist) return [];
    const names = [artist.display_name, ...(rosterArtist?.aliases || [])]
      .filter(Boolean).map(n => n.toLowerCase().trim());
    const titles = allReleases.filter(r => {
      const rn = Array.isArray(r.artist) ? r.artist : typeof r.artist === 'string' ? r.artist.split(',') : [];
      return rn.map(n => n.trim().toLowerCase()).some(n => names.some(a => n.includes(a) || a.includes(n)));
    }).map(r => r.title).filter(Boolean);
    return [...new Set(titles)];
  }

  // ── EPK ──────────────────────────────────────────────────────────────
  function openEpkEditor() {
    const artistNames = [artist.display_name, ...(rosterArtist?.aliases || [])]
      .filter(Boolean).map(n => n.toLowerCase().trim());
    const artistReleases = allReleases.filter(r => {
      const names = Array.isArray(r.artist) ? r.artist
        : typeof r.artist === 'string' ? r.artist.split(',') : [];
      return names.map(n => n.trim().toLowerCase())
        .some(n => artistNames.some(a => n.includes(a) || a.includes(n)));
    }).sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0)).slice(0, 12);

    setEpkData({
      bio: bio || '',
      tagline: '',
      pressPhoto: (photoUrl && !photoUrl.startsWith('blob:') ? photoUrl : '') || rosterArtist?.image || '',
      selectedReleases: artistReleases.map(r => r.slug || r.title),
      allReleases: artistReleases,
      socials: { ...socials },
      theme: previewSnapshot?.theme || theme || 'dark',
      artistName: rosterArtist?.displayName || artist.display_name || '',
      language: 'en',
      epkTheme: previewSnapshot?.theme || theme || 'dark',
      featuredVideos: ['', '', '', ''],
    });
    setShowEpk(true);
  }

  function buildAndOpenEpk(data) {
    const name    = data.artistName.toUpperCase();
    const themeId = data.epkTheme || data.theme || 'dark';
    const isHe    = data.language === 'he';
    const dir     = isHe ? 'rtl' : 'ltr';
    const align   = isHe ? 'right' : 'left';
    const dateStr = new Date().toLocaleDateString(isHe ? 'he-IL' : 'en-GB', { month: 'long', year: 'numeric' });

    const T_MAP = {
      dark:     { bg: '#000000', fg: '#f0ede8', muted: '#888', border: 'rgba(240,237,232,0.1)', accent: 'rgba(240,237,232,0.06)', headerBg: '#0a0a0a' },
      light:    { bg: '#ffffff', fg: '#0a0a0a', muted: '#777', border: 'rgba(0,0,0,0.1)',         accent: 'rgba(0,0,0,0.03)',       headerBg: '#f5f3ef' },
      slate:    { bg: '#0f1117', fg: '#e8eaf0', muted: '#8a8fa8', border: 'rgba(232,234,240,0.1)', accent: 'rgba(232,234,240,0.05)', headerBg: '#171b24' },
      crimson:  { bg: '#0d0305', fg: '#f5c6c0', muted: '#a07070', border: 'rgba(245,198,192,0.1)', accent: 'rgba(245,198,192,0.05)', headerBg: '#150608' },
      forest:   { bg: '#050d07', fg: '#c8e6c0', muted: '#6a9060', border: 'rgba(200,230,192,0.1)', accent: 'rgba(200,230,192,0.05)', headerBg: '#081209' },
      dusk:     { bg: '#0e0812', fg: '#e8d4f5', muted: '#8060a0', border: 'rgba(232,212,245,0.1)', accent: 'rgba(232,212,245,0.05)', headerBg: '#140c1c' },
      sand:     { bg: '#f2ede4', fg: '#2a1f0a', muted: '#8a7a60', border: 'rgba(42,31,10,0.1)',    accent: 'rgba(42,31,10,0.04)',    headerBg: '#ebe4d8' },
      midnight: { bg: '#080c1a', fg: '#c8d4f0', muted: '#6070a0', border: 'rgba(200,212,240,0.1)', accent: 'rgba(200,212,240,0.05)', headerBg: '#0d1226' },
    };
    const T = T_MAP[themeId] || T_MAP.dark;

    // YouTube videos — up to 4
    const videos = (data.featuredVideos || [])
      .map(url => {
        if (!url || !url.trim()) return null;
        const m = url.match(/(?:v=|youtu\.be\/|shorts\/|embed\/)([A-Za-z0-9_-]{11})/);
        if (!m) return null;
        const id = m[1];
        return { id, thumb: 'https://img.youtube.com/vi/' + id + '/mqdefault.jpg', url: 'https://www.youtube.com/watch?v=' + id };
      })
      .filter(Boolean)
      .slice(0, 4);

    // social links with SVG icons
    const SOCIAL_META = {
      spotify:    { label:'Spotify',      icon:'<svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm4.586 14.424a.622.622 0 01-.857.207c-2.348-1.432-5.304-1.756-8.785-.963a.622.622 0 11-.277-1.215c3.809-.87 7.076-.496 9.712 1.114a.623.623 0 01.207.857zm1.223-2.722a.779.779 0 01-1.072.257c-2.687-1.652-6.785-2.13-9.965-1.166a.779.779 0 11-.453-1.489c3.632-1.104 8.147-.569 11.233 1.327a.778.778 0 01.257 1.071zm.105-2.835C14.691 9.148 9.375 8.967 6.297 9.927a.935.935 0 11-.543-1.79c3.532-1.072 9.404-.865 13.115 1.337a.935.935 0 01-.955 1.593z"/></svg>' },
      appleMusic: { label:'Apple Music',  icon:'<svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M16.9 13.2c.03 3.3 2.9 4.4 2.93 4.42-.02.07-.46 1.58-1.52 3.13-.92 1.34-1.87 2.67-3.37 2.69-1.47.03-1.94-.87-3.61-.87-1.67 0-2.17.84-3.54.9-1.43.06-2.52-1.45-3.45-2.78-1.88-2.73-3.33-7.72-1.39-11.08.96-1.65 2.68-2.69 4.56-2.72 1.43-.03 2.78.96 3.61.96.83 0 2.49-1.18 4.21-1 .71.03 2.69.29 3.96 2.18-.1.06-2.36 1.38-2.38 4.37ZM14 3.4c.73-.89 1.22-2.14 1.08-3.4-1.05.04-2.36.7-3.12 1.59-.69.8-1.28 2.07-1.12 3.29 1.18.09 2.43-.6 3.16-1.48Z"/></svg>' },
      instagram:  { label:'Instagram',    icon:'<svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>' },
      youtube:    { label:'YouTube',      icon:'<svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M23.5 7.1s-.23-1.66-.94-2.39c-.9-.95-1.9-.96-2.36-1.02-3.3-.24-8.24-.24-8.24-.24h-.01s-4.95 0-8.24.24c-.46.06-1.46.07-2.36 1.02C.73 5.45.5 7.1.5 7.1S.27 9.1.27 11.1v1.78c0 2 .23 4 .23 4s.23 1.66.94 2.39c.9.95 2.08.92 2.61 1.03 1.89.18 8.05.24 8.05.24s4.96-.01 8.26-.25c.46-.06 1.46-.07 2.36-1.02.71-.73.94-2.39.94-2.39s.23-2 .23-4v-1.78c0-2-.23-4-.23-4ZM9.84 13.88V8.1l5.67 2.9-5.67 2.88Z"/></svg>' },
      tiktok:     { label:'TikTok',       icon:'<svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.78 1.52V6.76a4.85 4.85 0 01-1.01-.07z"/></svg>' },
      soundcloud: { label:'SoundCloud',   icon:'<svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M11.56 8.87V17h8.76c1.47 0 2.68-1.2 2.68-2.68 0-1.42-1.11-2.58-2.51-2.67a4.5 4.5 0 00-4.43-5.27c-1.23 0-2.36.5-3.18 1.3a.5.5 0 00-.32-.01zM0 14.22c0 1.54 1.25 2.78 2.78 2.78s2.78-1.25 2.78-2.78V9.78A2.78 2.78 0 000 12.56v1.66zm5.56 0V9.11a2.22 2.22 0 014.44 0v5.11a2.22 2.22 0 01-4.44 0z"/></svg>' },
      website:    { label:'Website',      icon:'<svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/></svg>' },
    };

    const chosenReleases = data.allReleases.filter(r =>
      data.selectedReleases.includes(r.slug || r.title)
    );

    const socialLinks = Object.entries(data.socials || {})
      .filter(([, v]) => v && v.trim() && v !== 'PLACEHOLDER')
      .map(([k, v]) => {
        const m = SOCIAL_META[k];
        if (!m) return '';
        return '<a href="' + v + '" style="display:inline-flex;align-items:center;gap:5px;margin:0 16px 0 0;color:' + T.fg + ';text-decoration:none;font-size:12px;font-weight:500;opacity:0.75">' + m.icon + m.label + '</a>';
      }).join('');

    // 4-col compact release grid — each links to yensound.com/release/:slug
    const relCols = chosenReleases.map(r => {
      const slug = r.slug || encodeURIComponent(r.title);
      const releaseUrl = 'https://yensound.com/release/' + slug;
      return (
        '<a href="' + releaseUrl + '" style="display:block;text-decoration:none;color:inherit;break-inside:avoid;page-break-inside:avoid">' +
          (r.cover
            ? '<img src="' + r.cover + '" style="width:100%;aspect-ratio:1;object-fit:cover;display:block;margin-bottom:6px" />'
            : '<div style="width:100%;aspect-ratio:1;background:' + T.border + ';margin-bottom:6px"></div>') +
          '<div style="font-size:10px;font-weight:700;color:' + T.fg + ';line-height:1.3;margin-bottom:2px;overflow:hidden;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical">' + r.title + '</div>' +
          '<div style="font-size:9px;color:' + T.muted + '">' + (r.date ? r.date.slice(0,4) : '') + '</div>' +
        '</a>'
      );
    }).join('');

    const html =
      '<!DOCTYPE html><html lang="' + (isHe ? 'he' : 'en') + '" dir="' + dir + '"><head><meta charset="UTF-8">' +
      '<style>' +
      '@import url(\'https://fonts.googleapis.com/css2?family=Heebo:wght@300;400;500;700;900&display=swap\');' +
      '*{box-sizing:border-box;margin:0;padding:0}' +
      'html,body{background:' + T.bg + ';-webkit-print-color-adjust:exact;print-color-adjust:exact}' +
      'body{font-family:\'Heebo\',Arial,sans-serif;color:' + T.fg + ';max-width:860px;margin:0 auto}' +
      'a{color:inherit}' +
      '@media print{html,body{background:' + T.bg + '}@page{margin:0;size:auto}.no-print{display:none!important}*{page-break-inside:avoid!important;break-inside:avoid!important}}' +
      '</style></head><body>' +

      /* ── PRINT BUTTON ── */
      '<div class="no-print" style="position:fixed;bottom:24px;right:24px;z-index:99;display:flex;gap:8px">' +
        '<button onclick="window.print()" style="padding:12px 24px;background:' + T.fg + ';color:' + T.bg + ';border:none;font-family:Heebo,sans-serif;font-size:12px;font-weight:700;letter-spacing:0.06em;cursor:pointer;border-radius:6px;box-shadow:0 4px 20px rgba(0,0,0,0.4)">↓ Save PDF</button>' +
      '</div>' +

      /* ── HERO ── */
      '<div style="display:grid;grid-template-columns:1fr 1fr;min-height:320px;background:' + T.bg + '">' +
        /* photo */
        '<div style="overflow:hidden;position:relative">' +
          (data.pressPhoto
            ? '<img src="' + data.pressPhoto + '" style="width:100%;height:100%;object-fit:cover;object-position:top center;display:block" />'
            : '<div style="width:100%;height:100%;min-height:320px;background:' + T.accent + '"></div>') +
        '</div>' +
        /* name/bio panel */
        '<div style="padding:40px 36px;background:' + T.headerBg + ';display:flex;flex-direction:column;justify-content:space-between;direction:' + dir + ';text-align:' + align + '">' +
          '<div>' +
            '<div style="font-size:7px;font-weight:700;letter-spacing:0.45em;text-transform:uppercase;color:' + T.muted + ';margin-bottom:16px">YEN SOUND · EPK</div>' +
            '<div style="font-size:44px;font-weight:900;letter-spacing:-2.5px;line-height:0.88;color:' + T.fg + ';margin-bottom:' + (data.tagline ? '12px' : '20px') + '">' + name + '</div>' +
            (data.tagline ? '<div style="font-size:10px;font-weight:600;letter-spacing:0.15em;text-transform:uppercase;color:' + T.muted + ';margin-bottom:20px">' + data.tagline + '</div>' : '') +
            (data.bio ? '<p style="font-size:12px;font-weight:300;line-height:1.9;color:' + T.muted + ';max-width:280px">' + data.bio.split('\n').join('<br>') + '</p>' : '') +
          '</div>' +
          '<div style="font-size:9px;color:' + T.muted + ';margin-top:20px;letter-spacing:0.08em;opacity:0.6">' + dateStr + '</div>' +
        '</div>' +
      '</div>' +

      /* ── THIN RULE ── */
      '<div style="height:1px;background:' + T.border + '"></div>' +

      /* ── SOCIAL LINKS ── */
      (socialLinks
        ? '<div style="padding:16px 32px;border-bottom:1px solid ' + T.border + ';text-align:center">' + socialLinks + '</div>'
        : '') +

      /* ── SPOTLIGHT / FEATURED VIDEOS ── */
      (videos.length > 0
        ? '<div style="padding:24px 32px;border-bottom:1px solid ' + T.border + '">' +
            '<div style="font-size:7px;font-weight:700;letter-spacing:0.35em;text-transform:uppercase;color:' + T.muted + ';margin-bottom:14px;direction:' + dir + ';text-align:' + align + '">' + (isHe ? 'ספוטלייט' : 'FEATURED TRACK') + '</div>' +
            '<div style="display:grid;grid-template-columns:repeat(' + Math.min(videos.length, 4) + ',1fr);gap:8px">' +
              videos.map(v =>
                '<a href="' + v.url + '" style="display:block;position:relative;text-decoration:none;overflow:hidden">' +
                  '<img src="' + v.thumb + '" style="width:100%;aspect-ratio:16/9;object-fit:cover;display:block" />' +
                  '<div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,0.25)">' +
                    '<div style="width:32px;height:32px;background:rgba(255,255,255,0.85);border-radius:50%;display:flex;align-items:center;justify-content:center">' +
                      '<div style="width:0;height:0;border-style:solid;border-width:6px 0 6px 12px;border-color:transparent transparent transparent #000;margin-left:2px"></div>' +
                    '</div>' +
                  '</div>' +
                '</a>'
              ).join('') +
            '</div>' +
          '</div>'
        : '') +

      /* ── DISCOGRAPHY ── */
      (chosenReleases.length > 0
        ? '<div style="padding:28px 32px 36px;break-inside:avoid;page-break-inside:avoid">' +
            '<div style="font-size:7px;font-weight:700;letter-spacing:0.35em;text-transform:uppercase;color:' + T.muted + ';margin-bottom:16px;direction:' + dir + ';text-align:' + align + '">' + (isHe ? 'דיסקוגרפיה' : 'Discography') + '</div>' +
            '<div class="no-break" style="display:grid;grid-template-columns:repeat(4,1fr);gap:10px">' + relCols + '</div>' +
          '</div>'
        : '') +

      /* ── FOOTER ── */
      '<div style="padding:12px 32px;border-top:1px solid ' + T.border + ';display:flex;justify-content:space-between;align-items:center;background:' + T.headerBg + '">' +
        '<a href="https://yensound.com" style="font-size:8px;font-weight:700;letter-spacing:0.4em;text-transform:uppercase;color:' + T.muted + ';text-decoration:none">YEN SOUND</a>' +
        '<span style="font-size:9px;color:' + T.muted + ';opacity:0.6">yensound.com</span>' +
      '</div>' +
      '</body></html>';

    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
    w.focus();
  }





    function buildSnapshot(overrides = {}) {
    return { bio, customButtons, buttonOrder, theme, photoUrl, ...overrides };
  }
  function refreshPreview(overrides = {}) {
    setPreviewSnapshot(buildSnapshot(overrides));
  }

  useEffect(() => {
    async function fetchArtist() {
      const { data, error } = await supabase.from('artists').select('*').eq('id', artistId).single();
      if (!error && data) {
        setArtist(data);
        setBio(data.bio || '');
        setPhotoUrl(data.profile_image || '');
        setTheme(data.theme || 'dark');
        // Fetch merch preview using the artist slug from roster
        if (data.slug) {
          supabase.from('merch').select('name,image_url,price,currency,badge,locked_until,sold_out')
            .eq('active', true).eq('artist_slug', data.slug).limit(6)
            .then(({ data: mData }) => setPreviewMerchItems(mData || []));
        }
        setShowMerch(data.show_merch !== false);
        const migrated = migrateEmbedUrl(data.custom_buttons || [], data.embed_url || '');
        setCustomButtons(migrated);
        const base = data.button_order?.length ? data.button_order : DEFAULT_ORDER;
        const order = [...base, ...migrated.map(b => b.id).filter(id => !base.includes(id))];
        setButtonOrder(order);
        setPreviewSnapshot({ bio: data.bio || '', customButtons: migrated, buttonOrder: order, theme: data.theme || 'dark', photoUrl: data.profile_image || '' });
      }
      setLoading(false);
    }
    fetchArtist();
  }, [artistId]);

  useEffect(() => {
    setButtonOrder(prev => {
      const newIds = customButtons.map(b => b.id).filter(id => !prev.includes(id));
      return newIds.length ? [...prev, ...newIds] : prev;
    });
  }, [customButtons]);

  // ── Activity logger ──
  async function logActivity(action, detail) {
    try {
      const rosterEntry = roster.find(r => r.slug === artistId);
      const artistDisplayName = rosterEntry?.displayName || rosterEntry?.name || artistId;
      await supabase.from('artist_activity').insert([{
        artist_slug: artistId, artist_name: artistDisplayName, action, detail: detail || null,
      }]);
    } catch (_) { /* non-blocking */ }
  }

  async function saveBio(value, explicit) {
    setBio(value);
    if (!explicit) return; // blur just updates local, explicit Save button actually saves
    setBioStatus('saving'); setSaveError(null);
    const { error } = await supabase.from('artists').update({ bio: value }).eq('id', artistId);
    if (error) { setSaveError(error.message); setBioStatus('error'); }
    else { setBioStatus('saved'); refreshPreview({ bio: value }); logActivity('bio_update'); }
    setTimeout(() => setBioStatus('idle'), 2500);
  }

  async function savePhoto(url) {
    setPhotoStatus('saving'); setSaveError(null);
    const { error } = await supabase.from('artists').update({ profile_image: url || null }).eq('id', artistId);
    if (error) { setSaveError(error.message); setPhotoStatus('error'); }
    else { setPhotoStatus('saved'); setPhotoUrl(url); setPreviewSnapshot(p => ({ ...(p || buildSnapshot()), photoUrl: url })); setTimeout(() => setPhotoStatus('idle'), 2500); logActivity('photo_update'); }
  }

  async function handlePhotoFile(file) {
    if (!file) return;
    if (CLOUDINARY_CLOUD_NAME === 'YOUR_CLOUD_NAME') { alert('Cloudinary not configured — paste a URL instead.'); return; }
    setPhotoUrl(URL.createObjectURL(file));
    setPhotoUploading(true); setPhotoProgress(0);
    try {
      const url = await uploadSquarePhoto(file, `yen-sound/artists/${artistId}`, setPhotoProgress);
      setPhotoUploading(false);
      await savePhoto(url);
    } catch (err) { setPhotoUploading(false); setPhotoStatus('error'); alert('Upload failed: ' + err.message); }
  }

  async function saveTheme(val) {
    setTheme(val); setThemeStatus('saving');
    const { error } = await supabase.from('artists').update({ theme: val }).eq('id', artistId);
    if (error) { setThemeStatus('error'); alert('Save error: ' + error.message); }
    else { setThemeStatus('saved'); setPreviewSnapshot(p => ({ ...(p || buildSnapshot()), theme: val })); setTimeout(() => setThemeStatus('idle'), 2500); logActivity('theme_change', val); }
  }

  async function saveButtons() {
    setBtnStatus('saving'); setSaveError(null);
    const { error } = await supabase.from('artists').update({ custom_buttons: customButtons, button_order: buttonOrder }).eq('id', artistId);
    if (error) { setSaveError(error.message); setBtnStatus('error'); }
    else { setBtnStatus('saved'); refreshPreview(); logActivity('buttons_update'); }
    setTimeout(() => setBtnStatus('idle'), 2500);
  }
  async function toggleMerch() {
    const next = !showMerch;
    setShowMerch(next);
    await supabase.from('artists').update({ show_merch: next }).eq('id', artistId);
    logActivity('merch_toggle', next ? 'on' : 'off');
  }


  async function resetPage() {
    if (!window.confirm('Remove all custom buttons, embeds, and reset order?')) return;
    setResetStatus('saving'); setSaveError(null);
    const { error } = await supabase.from('artists').update({ custom_buttons: [], embed_url: '', button_order: DEFAULT_ORDER }).eq('id', artistId);
    if (error) { setSaveError(error.message); setResetStatus('error'); }
    else { setCustomButtons([]); setButtonOrder(DEFAULT_ORDER); setResetStatus('saved'); refreshPreview({ customButtons: [], buttonOrder: DEFAULT_ORDER }); logActivity('reset'); }
    setTimeout(() => setResetStatus('idle'), 2500);
  }

  const addItem = useCallback((type) => {
    const id = uid();
    setCustomButtons(prev => [...prev,
      type === 'embed'  ? { id, type: 'embed',  url: '', label: '' } :
      type === 'locked' ? { id, type: 'locked', url: '', label: '', password: '' } :
                          { id, type: 'link',   url: '', label: '', icon: '' }
    ]);
  }, []);

  // flush is called from ButtonItemCard on blur — merges partial fields into the item
  const flushItem = useCallback((id, fields) => {
    setCustomButtons(prev => prev.map(b => b.id === id ? { ...b, ...fields } : b));
  }, []);

  const removeItem = useCallback((id) => {
    setCustomButtons(prev => prev.filter(b => b.id !== id));
    setButtonOrder(prev => prev.filter(k => k !== id));
  }, []);

  const changeItemType = useCallback((id, newType) => {
    setCustomButtons(prev => prev.map(b => b.id === id ? { ...b, type: newType, label: '', url: '', password: '' } : b));
  }, []);

  if (loading) return <div style={centered}><p style={{ fontFamily: F, fontSize: '10px', letterSpacing: '0.3em', textTransform: 'uppercase', opacity: 0.3, color: '#f0ede8' }}>Loading</p></div>;
  if (!artist) return (
    <div style={centered}>
      <p style={{ fontFamily: F, fontSize: '10px', letterSpacing: '0.3em', textTransform: 'uppercase', opacity: 0.4, color: '#f0ede8' }}>Artist not found</p>
      <Link to="/" style={{ color: '#f0ede8', fontFamily: F, fontSize: '10px', letterSpacing: '0.25em', textTransform: 'uppercase', marginTop: '24px', opacity: 0.35, textDecoration: 'none' }}>Back</Link>
    </div>
  );

  const folders = [
    { id: 'submit',       label: 'Submit a Release', icon: '＋', href: 'https://docs.google.com/forms/d/e/1FAIpQLSe8rH0NRf1YBN-rD78uuzIoLxwZjJAl4qBKPn7tQ0hZeNr59w/viewform?usp=header' },
    { id: 'distribution', label: 'Distribution Form', icon: '✍', action: () => setShowFormSigner(true) },
    { id: 'vault',        label: 'Vault',             icon: '◈', href: artist.upload_url },
    { id: 'releases',     label: 'My Releases',       icon: '♫', href: `/releases?artist=${encodeURIComponent(artist.filter_name || artistId)}`, internal: true },
    { id: 'presskit',     label: 'Press Kit',         icon: '⎘', action: () => openEpkEditor() },
    { id: 'editpage',     label: 'Edit My Page',      icon: '✎', action: () => { setActivePanel(null); setShowPreview(true); } },
  ];

  const tileHov   = e => { e.currentTarget.style.borderColor = 'rgba(240,237,232,0.5)'; e.currentTarget.style.background = '#0a0a0a'; };
  const tileUnhov = e => { e.currentTarget.style.borderColor = 'rgba(240,237,232,0.15)'; e.currentTarget.style.background = 'transparent'; };

  const fullOrderList = buttonOrder.filter(key => PLATFORM_META[key] || customButtons.find(b => b.id === key));

  const snap = previewSnapshot || buildSnapshot();
  const miniPreviewProps = {
    theme: snap.theme, bio: snap.bio, buttonOrder: snap.buttonOrder,
    customButtons: snap.customButtons, socials,
    photoUrl: snap.photoUrl,
    artistDisplayName: (rosterArtist?.displayName || artist.display_name || '').toUpperCase(),
    currentPhoto: (snap.photoUrl && !snap.photoUrl.startsWith('blob:') ? snap.photoUrl : null) || rosterArtist?.image || null,
    merchItems: previewMerchItems,
    showMerch,
  };

  return (
    <div style={{ backgroundColor: '#000', minHeight: '100vh', color: '#f0ede8', maxWidth: '480px', margin: '0 auto', paddingBottom: 'env(safe-area-inset-bottom)' }}>

      {/* ── Distribution form: fill, sign, submit ── */}
      {showFormSigner && (
        <DistributionFormSigner
          artistId={artistId}
          artistName={rosterArtist?.displayName || artist.display_name || ''}
          releaseTitles={artistReleaseTitles()}
          onClose={() => setShowFormSigner(false)}
          onSubmitted={count => logActivity('distribution_form_submitted', `${count} tracks`)}
        />
      )}

      {/* ── EPK Editor Overlay ── */}
      {showEpk && epkData && (
        <EpkEditor
          data={epkData}
          setData={setEpkData}
          onClose={() => setShowEpk(false)}
          onGenerate={() => { buildAndOpenEpk(epkData); }}
          uploadPhoto={async (file) => {
            if (CLOUDINARY_CLOUD_NAME === 'YOUR_CLOUD_NAME') return null;
            const fd = new FormData();
            fd.append('file', file);
            fd.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
            fd.append('folder', 'yen-sound/epk');
            const res = await fetch('https://api.cloudinary.com/v1_1/' + CLOUDINARY_CLOUD_NAME + '/image/upload', { method: 'POST', body: fd });
            const json = await res.json();
            return json.secure_url || null;
          }}
          D={D}
          F={F}
        />
      )}

      {/* ── Editor: always-visible preview + accordion ── */}
      {showPreview && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: D.bg, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>

          {/* sticky header */}
          <div style={{ position: 'sticky', top: 0, zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', paddingTop: 'calc(60px + env(safe-area-inset-top))', background: D.bg, borderBottom: `1px solid ${D.border}` }}>
            <p style={{ fontFamily: F, fontSize: '9px', letterSpacing: '0.3em', textTransform: 'uppercase', color: D.muted }}>Edit My Page</p>
            <button onClick={() => setShowPreview(false)}
              style={{ background: 'transparent', border: `1px solid ${D.border}`, color: D.muted, cursor: 'pointer', fontFamily: F, fontSize: '9px', letterSpacing: '0.2em', textTransform: 'uppercase', padding: '8px 16px', borderRadius: D.radiusSm, minHeight: '36px' }}>
              Done
            </button>
          </div>

          {/* ── desktop: side-by-side | mobile: stacked ── */}
          <div className="dash-editor-layout">

            {/* preview column — sticky on desktop, inline on mobile */}
            <div className="dash-preview-sticky">
              <p style={{ fontFamily: F, fontSize: '8px', letterSpacing: '0.25em', textTransform: 'uppercase', color: D.muted, textAlign: 'center', marginBottom: '12px', opacity: 0.5 }}>Preview</p>
              <MiniPreview {...miniPreviewProps} />
              <div style={{ display: 'flex', justifyContent: 'center', gap: '14px', marginTop: '14px', flexWrap: 'wrap' }}>
                <button onClick={() => refreshPreview()}
                  style={{ background: 'transparent', border: `1px solid ${D.border}`, color: D.muted, fontFamily: F, fontSize: '8px', letterSpacing: '0.2em', textTransform: 'uppercase', cursor: 'pointer', padding: '6px 12px', borderRadius: D.radiusSm }}>
                  Refresh
                </button>
                {artist.slug && (
                  <a href={`/artist/${artist.slug}`} target="_blank" rel="noreferrer"
                    style={{ fontFamily: F, fontSize: '8px', letterSpacing: '0.2em', textTransform: 'uppercase', color: D.muted, textDecoration: 'none' }}>
                    Open page →
                  </a>
                )}
              </div>
            </div>

            {/* editor column */}
            <div className="dash-editor-col">

          {saveError && (
            <div style={{ margin: '0 0 12px', padding: '12px 16px', border: '1px solid rgba(220,80,80,0.4)', background: 'rgba(220,80,80,0.06)', borderRadius: D.radiusSm }}>
              <p style={{ fontFamily: F, fontSize: '10px', color: 'rgba(255,120,120,0.9)', lineHeight: 1.6 }}>Save failed — {saveError}</p>
            </div>
          )}

          {/* accordion */}
          <div>

            <Section id="bio" activePanel={activePanel} setActivePanel={setActivePanel} label="Bio" icon="✎">
              <BioEditor initialBio={bio} onSave={saveBio} saveStatus={bioStatus} />
            </Section>
            <Section id="photo" activePanel={activePanel} setActivePanel={setActivePanel} label="Photo" icon="◻">
              <div onClick={() => photoFileRef.current?.click()}
                style={{ width: '100px', height: '100px', margin: '8px auto 14px', overflow: 'hidden', background: '#111', border: '1px solid rgba(240,237,232,0.2)', cursor: 'pointer', position: 'relative' }}>
                {photoUrl
                  ? <img src={photoUrl} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.2, fontFamily: F, fontSize: '9px' }}>No Photo</div>
                }
                {photoUploading && (
                  <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                    <div style={{ width: '56px', height: '2px', background: '#222' }}><div style={{ width: `${photoProgress}%`, height: '100%', background: '#f0ede8', transition: 'width 0.2s' }} /></div>
                    <span style={{ fontFamily: F, fontSize: '8px', color: '#f0ede8', opacity: 0.7 }}>{photoProgress}%</span>
                  </div>
                )}
                {!photoUploading && (
                  <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.55)', padding: '3px 0', textAlign: 'center' }}>
                    <span style={{ fontFamily: F, fontSize: '7px', letterSpacing: '0.12em', color: '#f0ede8', opacity: 0.6, textTransform: 'uppercase' }}>Tap to Upload</span>
                  </div>
                )}
              </div>
              <input ref={photoFileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handlePhotoFile(e.target.files?.[0])} />
              <p style={{ fontFamily: F, fontSize: '8px', opacity: 0.3, letterSpacing: '0.08em', lineHeight: 1.6, textAlign: 'center', marginBottom: '12px' }}>Any shape → auto-cropped square, face-aware.</p>
              <p style={{ fontFamily: F, fontSize: '9px', letterSpacing: '0.3em', textTransform: 'uppercase', opacity: 0.35, marginBottom: '6px' }}>Or paste image URL</p>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input value={typeof photoUrl === 'string' && photoUrl.startsWith('blob:') ? '' : photoUrl}
                  onChange={e => setPhotoUrl(e.target.value)} placeholder="https://..."
                  style={{ ...inputStyle, flex: 1 }} onFocus={focusBorder} onBlur={blurBorder} />
                <button onClick={() => savePhoto(photoUrl.trim())} disabled={photoStatus === 'saving' || photoUploading}
                  style={{ flexShrink: 0, padding: '10px 14px', background: 'transparent', border: '1px solid rgba(240,237,232,0.5)', color: '#f0ede8', fontFamily: F, fontSize: '9px', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', cursor: 'pointer', opacity: (photoStatus === 'saving' || photoUploading) ? 0.4 : 1 }}>
                  {photoStatus === 'saving' ? '...' : photoStatus === 'saved' ? 'Saved' : 'Save'}
                </button>
              </div>
              {artist.profile_image && (
                <button onClick={() => savePhoto('')} style={{ marginTop: '8px', background: 'none', border: 'none', color: 'rgba(220,80,80,0.6)', fontFamily: F, fontSize: '9px', letterSpacing: '0.15em', textTransform: 'uppercase', cursor: 'pointer', padding: 0 }}>Remove photo</button>
              )}
            </Section>

            <Section id="theme" activePanel={activePanel} setActivePanel={setActivePanel} label="Theme" icon="◑">
              <p style={{ fontFamily: F, fontSize: '9px', opacity: 0.4, letterSpacing: '0.08em', lineHeight: 1.7, marginBottom: '14px' }}>Choose a theme for your public page.</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginBottom: '12px' }}>
                {THEME_PRESETS.map(preset => (
                  <button key={preset.id} onClick={() => saveTheme(preset.id)} style={{
                    padding: '0', border: theme === preset.id ? `2px solid rgba(240,237,232,0.8)` : '1px solid rgba(240,237,232,0.1)',
                    background: 'transparent', cursor: 'pointer', display: 'flex', flexDirection: 'column',
                    overflow: 'hidden', transition: 'border-color 0.15s',
                  }}>
                    <div style={{ display: 'flex', height: '36px', width: '100%' }}>
                      <div style={{ flex: 1, background: preset.preview[0] }} />
                      <div style={{ flex: 1, background: preset.preview[1] }} />
                    </div>
                    <div style={{ padding: '5px 4px', background: '#050505', textAlign: 'center' }}>
                      <span style={{ fontFamily: F, fontSize: '8px', fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#f0ede8', opacity: theme === preset.id ? 1 : 0.45 }}>{preset.label}</span>
                      {theme === preset.id && <div style={{ fontFamily: F, fontSize: '6px', letterSpacing: '0.15em', color: 'rgba(100,255,180,0.85)', textTransform: 'uppercase', marginTop: '2px' }}>Active</div>}
                    </div>
                  </button>
                ))}
              </div>
              {themeStatus === 'saved' && <p style={{ fontFamily: F, fontSize: '9px', color: 'rgba(100,255,180,0.85)', letterSpacing: '0.1em', textAlign: 'center' }}>Saved</p>}
            </Section>

            <Section id="buttons" activePanel={activePanel} setActivePanel={setActivePanel} label="Links & Embeds" icon="⊞">
              {customButtons.length === 0 && <p style={{ fontFamily: F, fontSize: '10px', opacity: 0.25, textAlign: 'center', padding: '12px 0' }}>No items yet — add below</p>}
              {customButtons.map((item, i) => (
                <ButtonItemCard
                  key={item.id}
                  item={item}
                  index={i}
                  isActive={customActiveIdx === i}
                  dragHandlers={customHandlers}
                  onFlush={flushItem}
                  onRemove={removeItem}
                  onChangeType={changeItemType}
                  artistId={artistId}
                />
              ))}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', margin: '8px 0 14px' }}>
                {[['link','+ Link'], ['locked','+ Locked'], ['embed','+ Embed']].map(([type, label]) => (
                  <button key={type} onClick={() => addItem(type)}
                    style={{ padding: '12px 6px', background: 'transparent', border: '1px dashed rgba(240,237,232,0.2)', color: '#f0ede8', fontFamily: F, fontSize: '8px', letterSpacing: '0.15em', textTransform: 'uppercase', cursor: 'pointer' }}
                    onMouseOver={e => e.currentTarget.style.borderColor = 'rgba(240,237,232,0.5)'}
                    onMouseOut={e => e.currentTarget.style.borderColor = 'rgba(240,237,232,0.2)'}>
                    {label}
                  </button>
                ))}
              </div>
              <ActionBtn onClick={saveButtons} disabled={btnStatus === 'saving'}>
                {btnStatus === 'saving' ? 'Saving...' : btnStatus === 'saved' ? 'Saved' : 'Save'}
              </ActionBtn>
            </Section>

            <Section id="order" activePanel={activePanel} setActivePanel={setActivePanel} label="Button Order" icon="=">
              <p style={{ fontFamily: F, fontSize: '8px', opacity: 0.3, letterSpacing: '0.12em', lineHeight: 1.6, marginBottom: '12px' }}>Hold and drag to reorder. On mobile, hold 0.4s then drag.</p>
              {fullOrderList.length === 0 && <p style={{ fontFamily: F, fontSize: '10px', opacity: 0.25, textAlign: 'center', padding: '12px 0' }}>Add custom items first</p>}
              {fullOrderList.map((key, i) => (
                <OrderRow key={key} itemKey={key} index={i} customButtons={customButtons} socials={socials} dragHandlers={orderHandlers} isActive={orderActiveIdx === i} />
              ))}
              <div style={{ marginTop: '12px' }}>
                <ActionBtn onClick={saveButtons} disabled={btnStatus === 'saving'}>
                  {btnStatus === 'saving' ? 'Saving...' : btnStatus === 'saved' ? 'Saved' : 'Save Order'}
                </ActionBtn>
              </div>
            </Section>

            <Section id="merch" activePanel={activePanel} setActivePanel={setActivePanel} label="Merch on Page" icon="◑">
              <p style={{ fontFamily: F, fontSize: '11px', color: D.muted, lineHeight: 1.7, marginBottom: '14px' }}>
                Control whether your merch appears on your artist page.
              </p>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontFamily: F, fontSize: '11px', color: D.text }}>Show merch section</span>
                <button onClick={toggleMerch} style={{
                  fontFamily: F, fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em',
                  padding: '8px 18px', border: `1px solid ${showMerch ? 'rgba(74,222,128,0.5)' : D.border}`,
                  background: showMerch ? 'rgba(74,222,128,0.08)' : 'transparent',
                  color: showMerch ? 'rgba(74,222,128,0.9)' : D.muted,
                  borderRadius: D.radiusSm, cursor: 'pointer', transition: 'all 0.2s',
                }}>
                  {showMerch ? 'Visible' : 'Hidden'}
                </button>
              </div>
            </Section>

            <Section id="reset" activePanel={activePanel} setActivePanel={setActivePanel} label="Reset" icon="↺">
              <p style={{ fontFamily: F, fontSize: '10px', opacity: 0.5, letterSpacing: '0.08em', lineHeight: 1.8, marginBottom: '16px' }}>Removes all custom buttons, embeds, and resets order. Bio, photo, theme, streaming links and releases remain.</p>
              <ActionBtn onClick={resetPage} danger disabled={resetStatus === 'saving'}>
                {resetStatus === 'saving' ? 'Resetting...' : resetStatus === 'saved' ? 'Done' : 'Reset to Default'}
              </ActionBtn>
            </Section>


          </div>
          <div style={{ height: '80px' }} />
            </div>{/* end editor col */}
          </div>{/* end layout */}
        </div>
      )}

      <div style={{ paddingTop: 'calc(36px + env(safe-area-inset-top))' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
          <img src="/spinning yen logo white.gif" alt="YEN SOUND" className="yen-spin" style={{ width: '52px', height: '52px', opacity: 0.55 }} />
        </div>
        <div style={{ overflow: 'hidden', borderTop: '1px solid #1a1a1a', borderBottom: `1px solid ${D.border}`, padding: '7px 0' }}>
          <div style={{ display: 'inline-flex', animation: 'marquee 18s linear infinite', whiteSpace: 'nowrap' }}>
            {Array(6).fill('YEN SOUND ®   ').map((t, i) => (
              <span key={i} style={{ fontFamily: F, fontSize: '9px', fontWeight: 700, letterSpacing: '0.35em', textTransform: 'uppercase', opacity: 0.25, paddingRight: '40px' }}>{t}</span>
            ))}
          </div>
        </div>
      </div>

      <div style={{ padding: '40px 24px 32px', textAlign: 'center' }}>
        <h1 style={{ fontFamily: F, fontSize: '17px', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#f0ede8', marginBottom: '6px' }}>Welcome, {artist.display_name}</h1>
        <p style={{ fontFamily: F, fontSize: '9px', letterSpacing: '0.3em', textTransform: 'uppercase', opacity: 0.25 }}>Artist Dashboard</p>
      </div>

      <div style={{ padding: '0 24px' }}>
        <p style={{ fontFamily: F, fontSize: '9px', letterSpacing: '0.35em', textTransform: 'uppercase', opacity: 0.2, textAlign: 'center', marginBottom: '16px' }}>Quick Access</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
          {folders.map(folder => {
            const inner = (
              <>
                <span style={{ fontSize: '22px', lineHeight: 1, opacity: 0.7, marginBottom: '10px', display: 'block' }}>{folder.icon}</span>
                <span style={{ fontFamily: F, fontSize: '10px', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', opacity: 0.85 }}>{folder.label}</span>
              </>
            );
            const base = { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '32px 16px', minHeight: '110px', border: '1px solid rgba(240,237,232,0.15)', color: '#f0ede8', textDecoration: 'none', cursor: 'pointer', transition: 'border-color 0.2s, background 0.2s', background: 'transparent', WebkitTapHighlightColor: 'transparent' };
            if (folder.action) return <button key={folder.id} onClick={folder.action} style={{ ...base, border: '1px solid rgba(240,237,232,0.15)', fontFamily: 'inherit' }} onMouseEnter={tileHov} onMouseLeave={tileUnhov}>{inner}</button>;
            if (folder.download) return <a key={folder.id} href={folder.href} download style={base} onMouseEnter={tileHov} onMouseLeave={tileUnhov}>{inner}</a>;
            if (folder.internal) return <Link key={folder.id} to={folder.href} style={base} onMouseEnter={tileHov} onMouseLeave={tileUnhov}>{inner}</Link>;
            if (!folder.href) return <div key={folder.id} style={{ ...base, opacity: 0.3, cursor: 'default' }}>{inner}</div>;
            return <a key={folder.id} href={folder.href} target="_blank" rel="noopener noreferrer" style={base} onMouseEnter={tileHov} onMouseLeave={tileUnhov}>{inner}</a>;
          })}
        </div>
      </div>



      <div style={{ padding: '40px 24px 60px', textAlign: 'center', borderTop: '1px solid #1a1a1a', marginTop: '40px' }}>
        <Link to="/" style={{ fontFamily: F, fontSize: '10px', letterSpacing: '0.25em', textTransform: 'uppercase', color: '#f0ede8', opacity: 0.25, textDecoration: 'none', transition: 'opacity 0.2s' }}
          onMouseOver={e => e.currentTarget.style.opacity = 0.7} onMouseOut={e => e.currentTarget.style.opacity = 0.25}>
          Back to Home
        </Link>
      </div>
    </div>
  );
}

const centered = { minHeight: '100vh', backgroundColor: '#000', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px' };
